import { apiRequest } from '../lib/httpClient'
import { resolveEnvironmentName } from '../lib/environment'
import type {
  BulkUserUploadStartResponse,
  BulkUserUploadStatus,
  AgentBuilderDashboardAccessMode,
  AgentBuilderDashboardOption,
  ExistingUserConflict,
  GroupOption,
  ReportOption,
  UserActionResponse,
  UserEmailUpdateCheckResponse,
  UserExistsResponse,
  UserFormValues,
  UserGroupEditInit,
  UserGroupFormValues,
  UserGroupListResponse,
  UserGroupRow,
  UserGroupSaveResponse,
  UserListResponse,
  UserOption,
  UserRoleLabel,
  UserRow,
  UserStatusLabel,
} from '../types/users'
import { normalizePhone } from '../lib/phone'
import { asArray, pickNumber, pickString } from './shared/coercion'

const roleMap: Record<number, UserRoleLabel> = {
  1: 'SuperAdmin',
  2: 'Admin',
  3: 'User',
  4: 'User',
  5: 'Owner',
  7: 'Admin',
}

const statusMap: Record<number, UserStatusLabel> = {
  1: 'Active',
  2: 'Inactive',
  5: 'Invited',
  6: 'Expired',
  7: 'For Invite',
}

function pickArray<T>(value: unknown): T[] {
  return asArray<T>(value)
}

function mapUser(row: Record<string, unknown>): UserRow {
  const typeId = Number(row['typeId'] ?? row['TypeId'] ?? 3)
  const statusId = Number(row['statusId'] ?? row['StatusId'] ?? 1)
  const firstName = pickString(row['firstName'] ?? row['FirstName'])
  const lastName = pickString(row['lastName'] ?? row['LastName'])
  const fullName =
    pickString(row['fullName'] ?? row['FullName']) || `${firstName} ${lastName}`.trim() || pickString(row['email'] ?? row['Email'])

  const groupRecords = pickArray<Record<string, unknown>>(row['groups'] ?? row['Groups'])
  const groupIds = pickArray<string>(row['groupIds'] ?? row['GroupIds'])

  return {
    id: pickString(row['userId'] ?? row['UserId'] ?? row['id'] ?? row['Id']),
    firstName,
    lastName,
    fullName,
    phone: normalizePhone(pickString(row['phone'] ?? row['Phone'])),
    email: pickString(row['email'] ?? row['Email']),
    typeId,
    statusId,
    role: roleMap[typeId] || 'User',
    status: statusMap[statusId] || 'Inactive',
    groupIds,
    groups: groupRecords
      .map((g) => pickString(g['name'] ?? g['Name']))
      .filter(Boolean),
    twofa: Boolean(row['twofa'] ?? row['Twofa'] ?? row['isTwoFactorLogin'] ?? row['IsTwoFactorLogin']),
    isLocked: Boolean(row['isLocked'] ?? row['IsLocked']),
    lastLogin: (row['lastLogin'] as string) || (row['LastLogin'] as string) || null,
  }
}

function mapGroupOption(raw: Record<string, unknown>): GroupOption {
  return {
    value: pickString(raw['value'] ?? raw['Value'] ?? raw['groupId'] ?? raw['GroupId'] ?? raw['id'] ?? raw['Id']),
    label: pickString(raw['name'] ?? raw['Name'] ?? raw['label'] ?? raw['Label']),
    isCustomUserGroup: Boolean(raw['isCustomUserGroup'] ?? raw['IsCustomUserGroup']),
  }
}

function mapUserOption(raw: Record<string, unknown>): UserOption {
  return {
    value: pickString(raw['value'] ?? raw['Value'] ?? raw['userId'] ?? raw['UserId'] ?? raw['id'] ?? raw['Id']),
    label: pickString(raw['name'] ?? raw['Name'] ?? raw['label'] ?? raw['Label'] ?? raw['fullName'] ?? raw['FullName']),
    isInCustomUserGroup: Boolean(raw['isInCustomUserGroup'] ?? raw['IsInCustomUserGroup']),
    groupId: pickString(raw['groupId'] ?? raw['GroupId']),
    statusId: pickNumber(raw['statusId'] ?? raw['StatusId'], 0) || undefined,
  }
}

function mapReportOption(raw: Record<string, unknown>): ReportOption {
  return {
    value: pickString(raw['value'] ?? raw['Value'] ?? raw['reportId'] ?? raw['ReportId'] ?? raw['id'] ?? raw['Id']),
    label: pickString(raw['name'] ?? raw['Name'] ?? raw['label'] ?? raw['Label']),
  }
}

function mapAgentBuilderDashboardOption(raw: Record<string, unknown>): AgentBuilderDashboardOption {
  return {
    value: pickString(raw['value'] ?? raw['Value'] ?? raw['rootDashboardId'] ?? raw['RootDashboardId'] ?? raw['id'] ?? raw['Id']),
    label: pickString(raw['label'] ?? raw['Label'] ?? raw['title'] ?? raw['Title']),
    platformTypeId: pickNumber(raw['platformTypeId'] ?? raw['PlatformTypeId'], 0) || null,
    section: pickString(raw['section'] ?? raw['Section']) || null,
  }
}

function mapPlatformTypes(raw: unknown): number[] {
  return Array.from(new Set(pickArray<unknown>(raw)
    .map((value) => pickNumber(value, 0))
    .filter((value) => value > 0)))
}

function normalizeAgentBuilderDashboardAccessMode(value: unknown): AgentBuilderDashboardAccessMode {
  const raw = pickString(value).toLowerCase()
  if (raw === 'include') return 'Include'
  if (raw === 'exclude') return 'Exclude'
  return 'None'
}

function mapBulkUploadResultRow(raw: Record<string, unknown>) {
  return {
    row: Number(raw['row'] ?? raw['Row'] ?? 0),
    userId: pickString(raw['userId'] ?? raw['UserId']),
    status: Number(raw['status'] ?? raw['Status'] ?? 0),
    message: pickString(raw['message'] ?? raw['Message']),
  }
}

function mapBulkUploadStatus(raw: Record<string, unknown>): BulkUserUploadStatus {
  return {
    status: pickString(raw['status'] ?? raw['Status']),
    completionPercentage: Number(raw['completionPercentage'] ?? raw['CompletionPercentage'] ?? 0),
    client: pickString(raw['client'] ?? raw['Client']),
    processUser: pickString(raw['processUser'] ?? raw['ProcessUser']),
    processStartDate: pickString(raw['processStartDate'] ?? raw['ProcessStartDate']),
    results: pickArray<Record<string, unknown>>(raw['results'] ?? raw['Results']).map(mapBulkUploadResultRow),
  }
}

function mapUserGroupRow(raw: Record<string, unknown>): UserGroupRow {
  return {
    groupId: pickString(raw['groupId'] ?? raw['GroupId'] ?? raw['userGroupId'] ?? raw['UserGroupId'] ?? raw['id'] ?? raw['Id']),
    name: pickString(raw['name'] ?? raw['Name']),
    memberCount: Number(raw['memberCount'] ?? raw['MemberCount'] ?? raw['members'] ?? raw['Members'] ?? 0),
    reportCount: Number(raw['reportCount'] ?? raw['ReportCount'] ?? raw['reportsIncluded'] ?? raw['ReportsIncluded'] ?? 0),
    platformType: pickNumber(raw['platformType'] ?? raw['PlatformType'] ?? raw['platformTypeId'] ?? raw['PlatformTypeId'], 0) || null,
    insertDate: pickString(raw['insertDate'] ?? raw['InsertDate']) || null,
  }
}

function extractUserRows(payload: unknown) {
  const root = (payload || {}) as Record<string, unknown>
  const direct = pickArray<Record<string, unknown>>(root['users'])
  const gridObj = root['grids'] as Record<string, unknown> | undefined
  const altGridObj = root['Grids'] as Record<string, unknown> | undefined
  const grid = pickArray<Record<string, unknown>>(gridObj?.['users'])
  const altGrid = pickArray<Record<string, unknown>>(altGridObj?.['users'])
  const arrayPayload = Array.isArray(payload) ? (payload as Record<string, unknown>[]) : []
  return (grid.length ? grid : altGrid.length ? altGrid : direct.length ? direct : arrayPayload).map(mapUser)
}

function extractClientOptions(payload: Record<string, unknown>) {
  return ((payload['options'] || payload['Options'] || {}) as Record<string, unknown>) || {}
}

function extractClientGrids(payload: Record<string, unknown>) {
  return ((payload['grids'] || payload['Grids'] || {}) as Record<string, unknown>) || {}
}

type CreateUserApiBody = UserFormValues & {
  role?: string
  groups?: string[] | null
  defaultPageSize?: number
}

function buildCreateUserBody(payload: UserFormValues): CreateUserApiBody {
  const envName = resolveEnvironmentName()
  const normalizedPhone = normalizePhone(payload.phone)

  if (envName === 'staging') {
    return {
      clientId: '',
      firstName: payload.firstName,
      lastName: payload.lastName,
      fullName: '',
      phone: normalizedPhone,
      email: payload.email,
      typeId: payload.typeId,
      defaultClientId: payload.defaultClientId ?? payload.clientId ?? '',
      groupIds: payload.groupIds ?? [],
      reportIds: payload.reportIds ?? [],
      groups: undefined,
      defaultPageSize: 10,
      isrlc: payload.isrlc ?? false,
      isTwoFactorLogin: payload.isTwoFactorLogin ?? false,
      groupId: '',
      userOverride: payload.userOverride ?? false,
      ...(payload.userId ? { userId: payload.userId } : {}),
      ...(payload.allowedEnv ? { allowedEnv: payload.allowedEnv } : {}),
    }
  }

  return {
    ...payload,
    fullName: `${payload.firstName} ${payload.lastName}`.trim(),
    phone: normalizedPhone,
  }
}

export async function fetchUsers(
  filters: { fullName?: string; hideInactive?: boolean; clientId?: string } = {}
): Promise<UserListResponse> {
  let rows: UserRow[] = []
  let userGroups: UserGroupRow[] = []
  let groupOptions: GroupOption[] = []
  let reportOptions: ReportOption[] = []
  let usersForBulk: UserOption[] = []
  let org2FA = false

  if (filters.clientId) {
    const clientPayload = await apiRequest<Record<string, unknown>>('client/ui/detail/initialise', {
      method: 'GET',
      query: { clientId: filters.clientId },
    })
    const grids = extractClientGrids(clientPayload)
    const options = extractClientOptions(clientPayload)
    const userGrid = pickArray<Record<string, unknown>>(grids['users'])
    const groupGrid = pickArray<Record<string, unknown>>(grids['groups'])
    rows = userGrid.map(mapUser)
    userGroups = groupGrid.map(mapUserGroupRow)
    groupOptions = pickArray<Record<string, unknown>>(options['groups'] ?? options['userGroups']).map(mapGroupOption)
    reportOptions = pickArray<Record<string, unknown>>(options['reports'] ?? options['Reports']).map(mapReportOption)
    usersForBulk = pickArray<Record<string, unknown>>(options['users'] ?? options['userOptions']).map(mapUserOption)
    const client = (clientPayload?.['client'] ?? clientPayload?.['Client']) as Record<string, unknown> | undefined
    org2FA = Boolean(client?.['org2FA'] ?? client?.['Org2FA'])
  } else {
    const payload = await apiRequest<unknown>('user/ui/list/grid', { method: 'GET' })
    const root = (payload || {}) as Record<string, unknown>
    const options = ((root['options'] || root['Options'] || {}) as Record<string, unknown>) || {}
    const gridsObj = root['grids'] as Record<string, unknown> | undefined
    const altGridsObj = root['Grids'] as Record<string, unknown> | undefined
    rows = extractUserRows(payload)
    userGroups = pickArray<Record<string, unknown>>(
      gridsObj?.['groups'] ?? altGridsObj?.['groups'] ?? root['groups'] ?? root['Groups'] ?? root['userGroups']
    ).map(mapUserGroupRow)
    groupOptions = pickArray<Record<string, unknown>>(options['groups'] ?? options['userGroups']).map(mapGroupOption)
    reportOptions = pickArray<Record<string, unknown>>(options['reports'] ?? options['Reports']).map(mapReportOption)
    usersForBulk = pickArray<Record<string, unknown>>(options['users'] ?? options['userOptions']).map(mapUserOption)
  }

  let filteredRows = [...rows]
  if (filters.hideInactive) filteredRows = filteredRows.filter((row) => row.statusId === 1)
  if (filters.fullName?.trim()) {
    const query = filters.fullName.trim().toLowerCase()
    const phoneQuery = normalizePhone(query)
    filteredRows = filteredRows.filter(
      (row) =>
        row.fullName.toLowerCase().includes(query) ||
        row.email.toLowerCase().includes(query) ||
        (phoneQuery.length > 0 && normalizePhone(row.phone).includes(phoneQuery))
    )
  }

  return {
    rows: filteredRows,
    userGroups,
    groupOptions,
    reportOptions,
    bulkInviteUserOptions: usersForBulk,
    org2FA,
  }
}

export async function fetchUserDetail(userId: string): Promise<UserFormValues | null> {
  const payload = await apiRequest<Record<string, unknown>>('user/ui/detail/initialise', {
    method: 'GET',
    query: { userId },
  })
  const user = (payload?.['user'] ?? payload?.['User'] ?? payload?.['group1'] ?? payload?.['data']) as Record<string, unknown> | undefined
  if (!user) return null

  const firstName = pickString(user['firstName'] ?? user['FirstName'])
  const lastName = pickString(user['lastName'] ?? user['LastName'])
  const groupIds = pickArray<string>(user['groupIds'] ?? user['GroupIds'])
  const reportIds = pickArray<string>(user['reportIds'] ?? user['ReportIds'])

  return {
    userId: pickString(user['userId'] ?? user['UserId']) || userId,
    clientId: pickString(user['clientId'] ?? user['ClientId']),
    firstName,
    lastName,
    fullName: pickString(user['fullName'] ?? user['FullName']) || `${firstName} ${lastName}`.trim(),
    phone: normalizePhone(pickString(user['phone'] ?? user['Phone'])),
    email: pickString(user['email'] ?? user['Email']),
    statusId: pickNumber(user['statusId'] ?? user['StatusId'], 1),
    typeId: pickNumber(user['typeId'] ?? user['TypeId'], 3),
    defaultClientId: pickString(user['defaultClientId'] ?? user['DefaultClientId']),
    groupIds,
    reportIds,
    groupId: groupIds[0] ?? '',
    isrlc: Boolean(user['isrlc'] ?? user['Isrlc']),
    isTwoFactorLogin: Boolean(user['isTwoFactorLogin'] ?? user['IsTwoFactorLogin']),
    isLocked: Boolean(user['isLocked'] ?? user['IsLocked']),
    allowedEnv: pickString(user['allowedEnv'] ?? user['AllowedEnv']),
  }
}

export async function createUser(payload: UserFormValues) {
  const createBody = buildCreateUserBody(payload)
  delete createBody.statusId

  return apiRequest<UserActionResponse>('user/ui/create/save', {
    method: 'POST',
    body: createBody as unknown as Record<string, unknown>,
  })
}

export async function recreateUser(payload: UserFormValues) {
  const recreateBody = buildCreateUserBody(payload)
  delete recreateBody.statusId

  return apiRequest<UserActionResponse>('user/ui/create/recreate', {
    method: 'POST',
    body: recreateBody as unknown as Record<string, unknown>,
  })
}

export async function updateUser(payload: UserFormValues) {
  return apiRequest<UserActionResponse>('user/ui/edit/save', {
    method: 'POST',
    body: {
      ...payload,
      fullName: `${payload.firstName} ${payload.lastName}`.trim(),
      phone: normalizePhone(payload.phone),
      statusId: payload.statusId ?? 1,
    },
  })
}

export async function deleteUser(userId: string): Promise<void> {
  await apiRequest<void>(`user/ui/edit/delete/${userId}`, {
    method: 'POST',
    body: {},
  })
}

export async function checkUserExists(email: string) {
  const payload = await apiRequest<UserExistsResponse>(`user/ui/create/exists/${encodeURIComponent(email)}`, {
    method: 'GET',
  })
  const raw = payload as unknown as Record<string, unknown>
  return {
    exists: Boolean(payload.exists ?? raw['Exists']),
    pending: (payload.pending ?? raw['Pending']) as string | boolean | undefined,
    deleted: Boolean(payload.deleted ?? raw['Deleted']),
    userOverride: Boolean(payload.userOverride ?? raw['UserOverride']),
  } satisfies UserExistsResponse
}

export async function checkNewEmailStatus(email: string) {
  const payload = await apiRequest<UserEmailUpdateCheckResponse | boolean | string>('user/ui/check/newEmail/email', {
    method: 'GET',
    query: { email },
  })
  if (typeof payload === 'boolean') {
    return {
      hasEmailUpdate: payload,
    } satisfies UserEmailUpdateCheckResponse
  }

  if (typeof payload === 'string') {
    const normalized = payload.trim().toLowerCase()
    if (normalized === 'true' || normalized === 'false') {
      return {
        hasEmailUpdate: normalized === 'true',
      } satisfies UserEmailUpdateCheckResponse
    }
  }

  const raw = (payload && typeof payload === 'object' ? payload : {}) as Record<string, unknown>
  return {
    hasEmailUpdate: Boolean(raw['hasEmailUpdate'] ?? raw['HasEmailUpdate']),
  } satisfies UserEmailUpdateCheckResponse
}

export async function bulkInviteUsers(userIds: string[]) {
  return apiRequest<UserActionResponse>('invites/ui/user/bulkinvite', {
    method: 'POST',
    body: userIds,
  })
}

export async function reinviteUser(userId: string) {
  return apiRequest<UserActionResponse>('invites/ui/user/reinvite', {
    method: 'GET',
    query: { userId },
  })
}

export async function inviteUser(userId: string) {
  return apiRequest<UserActionResponse>('invites/ui/user/reinvite', {
    method: 'GET',
    query: { userId },
  })
}

export async function revokeUser(userId: string) {
  return apiRequest<UserActionResponse>('invites/ui/user/revoke', {
    method: 'GET',
    query: { userId },
  })
}

export async function unlockUser(userId: string) {
  return apiRequest<UserActionResponse>('user/ui/unlock', {
    method: 'GET',
    query: { userId },
  })
}

export async function resetUserAuthenticator(userId: string) {
  return apiRequest<{ success: boolean; reset: boolean }>('user/ui/mfa/reset', {
    method: 'POST',
    body: { userId },
  })
}

export async function downloadUsers(clientId: string) {
  const payload = await apiRequest<unknown>('user/ui/users/download', {
    method: 'GET',
    query: { clientId },
  })

  if (typeof payload === 'string') return payload
  const raw = (payload || {}) as Record<string, unknown>
  return pickString(raw['data'] ?? raw['Data'] ?? raw['value'] ?? raw['Value'] ?? raw['file'] ?? raw['File'])
}

export async function uploadUsers(payload: { formFile: string; contentType: string; clientId: string; inviteNewUsers: boolean }): Promise<BulkUserUploadStartResponse> {
  const response = await apiRequest<unknown>('user/ui/users/upload', {
    method: 'POST',
    body: payload,
  })
  const raw = (response || {}) as Record<string, unknown>
  return {
    success: Boolean(raw['success'] ?? raw['Success']),
    message: pickString(raw['message'] ?? raw['Message']),
    bulkUserUpdateQueueId: pickString(raw['bulkUserUpdateQueueId'] ?? raw['BulkUserUpdateQueueId']),
  }
}

export async function fetchBulkUserUploadStatus(queueId: string): Promise<{ success: boolean; message: string; data: BulkUserUploadStatus | null }> {
  const response = await apiRequest<unknown>(`user/ui/users/bulk-upload/${queueId}`, {
    method: 'GET',
  })
  const raw = (response || {}) as Record<string, unknown>
  const data = (raw['data'] ?? raw['Data']) as Record<string, unknown> | undefined

  return {
    success: Boolean(raw['success'] ?? raw['Success']),
    message: pickString(raw['message'] ?? raw['Message']),
    data: data ? mapBulkUploadStatus(data) : null,
  }
}

export async function updateUser2FA(payload: { UserId: string; DefaultClientId: string; IsTwoFactorLogin: boolean }) {
  return apiRequest<UserActionResponse>('user/ui/update/2FA', {
    method: 'POST',
    body: payload,
  })
}

export async function updateOrg2FA(payload: { isTwoFactorLogin: boolean; clientId: string }) {
  return apiRequest<UserActionResponse>('user/ui/profile/saveOrg2FA', {
    method: 'POST',
    body: payload,
  })
}

export async function fetchUserGroups(filters: { name?: string; clientId?: string } = {}): Promise<UserGroupListResponse> {
  let rowsRaw: Record<string, unknown>[] = []

  if (filters.clientId) {
    const payload = await apiRequest<Record<string, unknown>>('client/ui/detail/initialise', {
      method: 'GET',
      query: { clientId: filters.clientId },
    })
    const grids = extractClientGrids(payload)
    rowsRaw = pickArray<Record<string, unknown>>(grids['groups'])
  } else {
    const payload = await apiRequest<unknown>('group/ui/list/grid', { method: 'GET' })
    const root = (payload || {}) as Record<string, unknown>
    const gridsObj = root['grids'] as Record<string, unknown> | undefined
    const altGridsObj = root['Grids'] as Record<string, unknown> | undefined
    rowsRaw = Array.isArray(payload)
      ? (payload as Record<string, unknown>[])
      : pickArray<Record<string, unknown>>(
          gridsObj?.['groups'] ??
            altGridsObj?.['groups'] ??
            root['groups'] ??
            root['Groups'] ??
            root['userGroups']
        )
  }

  let rows = rowsRaw.map(mapUserGroupRow)
  if (filters.name?.trim()) {
    const q = filters.name.trim().toLowerCase()
    rows = rows.filter((r) => r.name.toLowerCase().includes(q))
  }
  return { rows }
}

export async function fetchUserGroupEditInit(groupId: string): Promise<UserGroupEditInit> {
  const payload = await apiRequest<Record<string, unknown>>('group/ui/edit/initialise', {
    method: 'GET',
    query: { groupId },
  })

  const groupPayload = (payload['group1'] ?? payload['group'] ?? payload['userGroup'] ?? payload['Group1']) as
    | Record<string, unknown>
    | undefined

  const options = (payload['options'] ?? payload['Options'] ?? {}) as Record<string, unknown>

  const group = groupPayload
    ? {
        groupId: pickString(groupPayload['groupId'] ?? groupPayload['GroupId'] ?? groupPayload['userGroupId'] ?? groupPayload['UserGroupId']),
        clientId: pickString(groupPayload['clientId'] ?? groupPayload['ClientId']),
        name: pickString(groupPayload['name'] ?? groupPayload['Name']),
        userIds: pickArray<string>(groupPayload['userIds'] ?? groupPayload['UserIds']),
        reportIds: pickArray<string>(groupPayload['reportIds'] ?? groupPayload['ReportIds']),
        billing: pickString(groupPayload['billing'] ?? groupPayload['Billing']),
        profitability: pickString(groupPayload['profitability'] ?? groupPayload['Profitability']),
        job: pickString(groupPayload['job'] ?? groupPayload['Job']),
        teamMember: pickString(groupPayload['teamMember'] ?? groupPayload['TeamMember']),
        wip: pickString(groupPayload['wip'] ?? groupPayload['WIP']),
        leads: pickString(groupPayload['leads'] ?? groupPayload['Leads']),
        quotes: pickString(groupPayload['quotes'] ?? groupPayload['Quotes']),
        dataIntegrity: pickString(groupPayload['dataIntegrity'] ?? groupPayload['DataIntegrity']),
        diyReport: pickString(groupPayload['diyReport'] ?? groupPayload['DiyReport']),
        purchaseOrders: pickString(groupPayload['purchaseOrders'] ?? groupPayload['PurchaseOrders']),
        snapshot: pickString(groupPayload['snapshot'] ?? groupPayload['Snapshot']),
        taxLodgements: pickString(groupPayload['taxLodgements'] ?? groupPayload['TaxLodgements']),
        xero: pickString(groupPayload['xero'] ?? groupPayload['Xero']),
        isCustomUserGroup: Boolean(groupPayload['isCustomUserGroup'] ?? groupPayload['IsCustomUserGroup']),
        platformType: pickNumber(groupPayload['platformType'] ?? groupPayload['PlatformType'], 0) || null,
        agentBuilderDashboardAccessMode: normalizeAgentBuilderDashboardAccessMode(
          groupPayload['agentBuilderDashboardAccessMode'] ?? groupPayload['AgentBuilderDashboardAccessMode']
        ),
        agentBuilderDashboardRootIds: pickArray<string>(
          groupPayload['agentBuilderDashboardRootIds'] ?? groupPayload['AgentBuilderDashboardRootIds']
        ),
      }
    : null

  const userOptions = pickArray<Record<string, unknown>>(options['users'] ?? options['Users']).map(mapUserOption)
  const reportOptions = pickArray<Record<string, unknown>>(options['reports'] ?? options['Reports']).map(mapReportOption)
  const agentBuilderDashboardOptions = pickArray<Record<string, unknown>>(
    options['agentBuilderDashboards'] ?? options['AgentBuilderDashboards']
  ).map(mapAgentBuilderDashboardOption)
  const platformTypes = mapPlatformTypes(options['platformTypes'] ?? options['PlatformTypes'])

  return { group, userOptions, reportOptions, agentBuilderDashboardOptions, platformTypes }
}

export async function fetchUserGroupCreateInit(clientId?: string): Promise<UserGroupEditInit> {
  const payload = await apiRequest<Record<string, unknown>>('group/ui/detail/initialise', {
    method: 'GET',
    query: clientId ? { clientId } : { groupId: '' },
  })
  const options = (payload['options'] ?? payload['Options'] ?? {}) as Record<string, unknown>
  const userOptions = pickArray<Record<string, unknown>>(options['users'] ?? options['Users']).map(mapUserOption)
  const reportOptions = pickArray<Record<string, unknown>>(options['reports'] ?? options['Reports']).map(mapReportOption)
  const agentBuilderDashboardOptions = pickArray<Record<string, unknown>>(
    options['agentBuilderDashboards'] ?? options['AgentBuilderDashboards']
  ).map(mapAgentBuilderDashboardOption)
  const platformTypes = mapPlatformTypes(options['platformTypes'] ?? options['PlatformTypes'])

  return { group: null, userOptions, reportOptions, agentBuilderDashboardOptions, platformTypes }
}

export async function createUserGroup(payload: UserGroupFormValues) {
  return apiRequest<UserGroupSaveResponse>('group/ui/create/save', {
    method: 'POST',
    body: payload,
  })
}

export async function updateUserGroup(payload: UserGroupFormValues) {
  return apiRequest<UserGroupSaveResponse>('group/ui/edit/save', {
    method: 'POST',
    body: payload,
  })
}

export async function deleteUserGroup(groupId: string): Promise<void> {
  await apiRequest<void>(`group/ui/edit/delete/${groupId}`, {
    method: 'POST',
    body: {},
  })
}

export function mapExistingUserConflicts(response: UserGroupSaveResponse): ExistingUserConflict[] {
  const conflicts: ExistingUserConflict[] = []
  for (const row of response.existingUserIds || []) {
    const hit = row.existCheck?.[0]
    if (!hit) continue
    conflicts.push({
      name: pickString(hit.name),
      groupName: pickString(hit.ugName),
    })
  }
  return conflicts
}
